'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Header } from '@/components/landing/header'
import { Footer } from '@/components/landing/footer'
import { FAQStructuredData } from '@/components/seo/structured-data'
import { Card } from '@/components/ui/card'
import {
  ArrowRight,
  Car,
  Home,
  Mail,
  Sparkles,
  TreePine,
  Users,
} from 'lucide-react'
import { MARKETING_TRAVEL_PRODUCT } from '@/components/landing/marketingTravelCopy'

export default function HelpPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)

  const faqs = [
    {
      question: 'What is Wagamour?',
      answer: `Wagamour is a Pet Mobility Platform that helps dog parents make better real-world decisions — from when to walk to where to go, who to meet, and what their dog needs. It uses your dog's profile, your location, and real-world conditions to provide clear, actionable guidance — not generic advice.`,
    },
    {
      question: 'How does Wagamour work?',
      answer: `Wagamour combines three inputs:
- Your dog's profile (breed, size, sensitivities, activity level)
- Your location
- Real-world conditions (weather, temperature, environment)

This powers five core experiences:
- Home — walk intelligence and safety guidance
- Play — nearby dogs and playdate coordination
- Parks — live park discovery
- Picks — contextual product recommendations
- Travel — dog-first journeys (now testing; signal availability varies by vehicle and provider)`,
    },
    {
      question: 'Why does Wagamour need my location?',
      answer: `Location is what makes Wagamour useful.

With location enabled, you get:
- Accurate walk safety insights
- Nearby parks and playdates
- Contextual recommendations based on your environment

Without it, the experience becomes limited and generic.`,
    },
    {
      question: "How does Wagamour use my dog's information?",
      answer: `Your dog's profile helps tailor recommendations to your specific dog — including heat sensitivity, activity level, size, and breed traits.

This improves walk guidance, park recommendations, and product suggestions.

Your data is used only to provide and improve the service — it is not sold to third parties.`,
    },
    {
      question: 'How do product recommendations work?',
      answer: `Wagamour surfaces products based on your dog's profile and current conditions, such as heat, cold, terrain, and activity level.

Some recommendations include affiliate links to trusted retailers. Wagamour may earn a commission at no extra cost to you.

All recommendations are suggestions — you should always use your own judgment.`,
    },
    {
      question: 'Is Wagamour free to use?',
      answer: `Yes. Wagamour is free to start.

Core features available today:
- Walk intelligence (Home)
- Playdates (Play)
- Park discovery (Parks)
- Product picks (Picks)
- Travel Intelligence (now testing; signal availability varies by vehicle and provider)`,
    },
    {
      question: 'Is Wagamour available everywhere?',
      answer: `Wagamour is currently U.S.-focused.

It works anywhere weather and location data are available, but park data and community signals may be stronger in certain areas.`,
    },
    {
      question: 'Is my data safe?',
      answer: `Yes.

Your data is encrypted, you control what you share, and you can delete your data at any time. Wagamour is designed with privacy and transparency in mind.`,
    },
    {
      question: 'How accurate are the recommendations?',
      answer: `Recommendations are based on real-time conditions and your dog's profile.

They are designed to be helpful guidance — not guarantees. Conditions can change quickly, so you should always use your own judgment.`,
    },
    {
      question: 'Can I use Wagamour without location enabled?',
      answer: `You can, but functionality will be limited.

Without location:
- parks won't be accurate
- playdates won't work properly
- recommendations become generic`,
    },
    {
      question: 'Can I use Wagamour for multiple dogs?',
      answer: `Yes.

Wagamour supports multiple dogs depending on your plan:
- Tail Wagger (Free) — 1 dog profile
- Pack Leader — up to 5 dog profiles

Each dog has its own profile, allowing recommendations to be tailored based on breed, size, sensitivities, and activity level.`,
    },
    {
      question: "What's coming next?",
      answer: `We're building the next layer of Wagamour, including a provider ecosystem (walkers, sitters, groomers, trainers), deeper personalization, and more proactive guidance.

These features are not live yet.`,
    },
    {
      question: 'What makes Wagamour different?',
      answer: `Most pet apps provide information.

Wagamour provides decisions.

It's designed around real-world variability — where conditions change and what's right for your dog depends on context.`,
    },
    {
      question: 'How do I get started?',
      answer: `Open the app at wagamour.com, create your dog's profile, enable location, and start exploring:

Home → Play → Parks → Picks → Travel`,
    },
  ]

  const quickTopics = [
    {
      icon: Home,
      title: 'Home — walk intelligence',
      body: 'Heat, paw, and surface guidance and “what to do” style tips for the outing.',
    },
    {
      icon: Users,
      title: 'Play — connections',
      body: 'Nearby dogs, compatibility, playdate requests.',
    },
    {
      icon: TreePine,
      title: 'Parks — discovery',
      body: 'Location-aware park lists with conditions and community input where available.',
    },
    {
      icon: Sparkles,
      title: 'Picks — products',
      body: 'Contextual gear ideas; affiliate or retailer links when you shop.',
    },
    {
      icon: Car,
      title: 'Travel — journeys',
      body: 'Prepare, understand conditions on the way, and arrive with more context for your dog.',
      nowTesting: true,
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-wag-primary/5 to-wag-secondary/5">
      <FAQStructuredData faqs={faqs} />
      <Header />

      <main className="container mx-auto px-4 pt-24 pb-16 md:pt-16 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold font-heading text-gray-900 mb-4">
            Help &amp; support
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            How the Pet Mobility Platform works today—no booking marketplace, no
            separate open-ended Q&amp;A product—just the core tabs: Home, Play,
            Parks, Picks, and Travel.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          {quickTopics.map((t) => {
            const Icon = t.icon
            return (
              <Card key={t.title} className="p-5 border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-purple-700 mb-2">
                  <Icon className="h-5 w-5" />
                  <span className="font-semibold text-gray-900 text-sm">
                    {t.title}
                  </span>
                  {'nowTesting' in t && t.nowTesting ? (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-amber-900">
                      Now testing
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t.body}
                </p>
              </Card>
            )
          })}
        </div>
        <p className="mb-14 text-center text-sm leading-relaxed text-gray-500">
          {MARKETING_TRAVEL_PRODUCT.disclosure}
        </p>

        <Card className="p-6 md:p-8 mb-12 border-gray-100 bg-white">
          <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">
            Contact
          </h2>
          <p className="text-gray-600 mb-4">
            For bugs, feedback, or account questions:
          </p>
          <a
            href="mailto:hello@wagamour.com"
            className="inline-flex items-center gap-2 text-purple-600 font-medium hover:text-purple-700"
          >
            <Mail className="h-4 w-4" />
            hello@wagamour.com
          </a>
          <p className="mt-4 text-sm text-gray-500">
            Providers: services are not live—use the{' '}
            <Link href="/#providers" className="text-purple-600 underline">
              waitlist on the homepage
            </Link>
            .
          </p>
        </Card>

        <h2 className="text-2xl font-bold font-heading text-gray-900 mb-6 text-center">
          Frequently asked questions
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <Card
              key={faq.question}
              className="overflow-hidden border-gray-100"
            >
              <button
                type="button"
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                className="w-full text-left flex items-center justify-between p-5 hover:bg-gray-50/80"
                aria-expanded={openFAQ === index}
              >
                <span className="font-semibold text-gray-900 pr-4">
                  {faq.question}
                </span>
                <ArrowRight
                  className={`h-5 w-5 text-gray-400 shrink-0 transition-transform ${
                    openFAQ === index ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {openFAQ === index && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base pt-4 whitespace-pre-line">
                    {faq.answer}
                  </p>
                </div>
              )}
            </Card>
          ))}
        </div>

        <p className="text-center mt-10 text-sm text-gray-500">
          More detail:{' '}
          <Link href="/how-it-works" className="text-purple-600 font-medium">
            How it works
          </Link>
        </p>
      </main>

      <Footer />
    </div>
  )
}
